import { usePostHogCapture } from '@app/core/integrations/posthog/usePostHogCapture';
import { useCallback } from 'react';
import Player from 'video.js/dist/types/player';

const POSTHOG_VIDEO_DOMAIN = 'video_player';

/** Video.js control bar has progressControl (seek bar); not in official Player types. */
type PlayerWithProgressControl = Player & {
  controlBar?: {
    progressControl?: {
      on: (e: string, fn: () => void) => void;
      off: (e: string, fn: () => void) => void;
    };
  };
};

/**
 * Returns a callback that accepts a Video.js player and registers event listeners
 * to capture interesting playback events to PostHog. The callback returns an
 * unsubscribe function that removes the listeners; call it on player dispose or unmount.
 *
 * Captured events: PiP enter/leave, fullscreen enter/leave, play, pause, snapshot taken, seekbar clicked (user only).
 */
export function useVideoCapture() {
  const capture = usePostHogCapture({ domain: POSTHOG_VIDEO_DOMAIN });

  return useCallback(
    (player: Player): (() => void) => {
      const onEnterPiP = () => capture('pip_entered');
      const onLeavePiP = () => capture('pip_left');
      const onFullscreenEnter = () => capture('fullscreen_entered');
      const onFullscreenExit = () => capture('fullscreen_exited');
      const onFullscreenChange = () => {
        if (player.isFullscreen()) {
          onFullscreenEnter();
        } else {
          onFullscreenExit();
        }
      };
      /** Fired by the snapshot plugin when user takes a snapshot (no image data sent to PostHog). */
      const onSnapshotTaken = () => capture('snapshot_taken');
      /** User clicked/tapped the seek bar (not programmatic seek). */
      const onSeekBarInteraction = () =>
        capture('seekbar_clicked', { current_time: player.currentTime() });

      player.on('enterpictureinpicture', onEnterPiP);
      player.on('leavepictureinpicture', onLeavePiP);
      player.on('fullscreenchange', onFullscreenChange);
      player.on('snapshotTaken', onSnapshotTaken);

      const progressControl = (player as PlayerWithProgressControl).controlBar?.progressControl;
      if (progressControl) {
        progressControl.on('mousedown', onSeekBarInteraction);
        progressControl.on('touchstart', onSeekBarInteraction);
      }

      return () => {
        if (player.isDisposed()) {
          return;
        }
        player.off('enterpictureinpicture', onEnterPiP);
        player.off('leavepictureinpicture', onLeavePiP);
        player.off('fullscreenchange', onFullscreenChange);
        player.off('snapshotTaken', onSnapshotTaken);
        if (progressControl) {
          progressControl.off('mousedown', onSeekBarInteraction);
          progressControl.off('touchstart', onSeekBarInteraction);
        }
      };
    },
    [capture],
  );
}
